import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  ChevronDownIcon,
  BeakerIcon,
  BookmarkIcon,
  CogIcon,
  AdjustmentsHorizontalIcon,
  ChartBarIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';

export interface NutritionQuickActions {
  onNewFeedMix: () => void;
  onOptimizeMix: () => void;
  onManageTemplates: () => void;
  onBatchOperations?: () => void;
  onAddFeedStock?: () => void;
  onViewReports?: () => void;
  batchCount?: number;
}

interface NutritionQuickActionsMenuProps extends NutritionQuickActions {}

const actionGroups = [
  {
    label: 'Record',
    items: [
      { key: 'mix', label: 'New feed mix', icon: BeakerIcon },
      { key: 'stock', label: 'Add feed stock', icon: CubeIcon },
      { key: 'batch', label: 'Batch operations', icon: AdjustmentsHorizontalIcon },
    ],
  },
  {
    label: 'Plan',
    items: [
      { key: 'templates', label: 'Mix templates', icon: BookmarkIcon },
      { key: 'optimize', label: 'Optimize mix', icon: CogIcon },
    ],
  },
  {
    label: 'Insights',
    items: [{ key: 'reports', label: 'Reports', icon: ChartBarIcon }],
  },
] as const;

export default function NutritionQuickActionsMenu({
  onNewFeedMix,
  onOptimizeMix,
  onManageTemplates,
  onBatchOperations,
  onAddFeedStock,
  onViewReports,
  batchCount = 0,
}: NutritionQuickActionsMenuProps) {
  const handlers: Record<string, () => void> = {
    mix: onNewFeedMix,
    stock: onAddFeedStock ?? (() => {}),
    batch: onBatchOperations ?? (() => {}),
    templates: onManageTemplates,
    optimize: onOptimizeMix,
    reports: onViewReports ?? (() => {}),
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button type="button" className="btn btn-secondary focus-pop text-sm">
        Quick actions
        <ChevronDownIcon className="ml-1 h-4 w-4" aria-hidden />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-20 mt-2 w-56 origin-top-right rounded-xl bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
          {actionGroups.map((group) => (
            <div key={group.label} className="py-1">
              <p className="px-4 py-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{group.label}</p>
              {group.items.map((item) => {
                if (item.key === 'batch' && (!onBatchOperations || batchCount === 0)) return null;
                if (item.key === 'stock' && !onAddFeedStock) return null;
                const Icon = item.icon;
                return (
                  <Menu.Item key={item.key}>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={handlers[item.key]}
                        className={`${active ? 'bg-slate-50' : ''} flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-700`}
                      >
                        <Icon className="h-4 w-4 text-slate-500" aria-hidden />
                        {item.label}
                        {item.key === 'batch' && batchCount > 0 && (
                          <span className="ml-auto rounded-full bg-cyan-100 px-2 py-0.5 text-xs font-medium text-cyan-800">
                            {batchCount}
                          </span>
                        )}
                      </button>
                    )}
                  </Menu.Item>
                );
              })}
            </div>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
